import { DeleteFilled, DislikeTwoTone, HistoryOutlined, LikeTwoTone, SafetyCertificateTwoTone, WarningTwoTone } from "@ant-design/icons";
import { Button, Pagination, Space, Table, Tooltip, Typography } from "antd";
import { maskValue } from "common/Utility";
import DataObjectLink from "components/atoms/DataObjectLink";
import FlexContainerLayout from "components/atoms/FlexContainerLayout";
import FormDisplayField from "components/atoms/FormDisplayField";
import SchemaFieldTitle from "components/atoms/SchemaFieldTitle";
import DataObjectShareOption from "containers/DataObjectShareOption";
import moment from "moment";
import { useEffect, useState } from "react";
import "./style.css";

const { Text } = Typography;
const { Column } = Table;

/** @type {import('./types').IDataObjectsPreviewList} */
const DataObjectsPreviewList = ({
    showHeader = true,
    recordCount,
    schemaFields,
    dataObjectList,
    loading,
    onPageSelect,
    onDataObjectSelected,
    onShowDataObjectHistory,
    onDeleteDataObject,
}) => {
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const [pagination, setPagination] = useState({ defaultPageSize: 100, pageSize: 100, page: 1 });
    const [paginatedDataObjectList, setPaginatedDataObjectList] = useState(dataObjectList && dataObjectList.slice(pagination.page - 1, pagination.pageSize));

    useEffect(() => {
        setPaginatedDataObjectList(dataObjectList && dataObjectList.slice((pagination.page - 1) * pagination.pageSize, pagination.page * pagination.pageSize));
    }, [dataObjectList, pagination.page, pagination.pageSize]);

    const onClickRow = (record, rowIndex) => {
        return {
            onClick: () => {
                if (selectedRowKeys.length === 0 || selectedRowKeys[0] !== record.dataObjectId) {
                    setSelectedRowKeys([record.dataObjectId]);
                    onDataObjectSelected && onDataObjectSelected(dataObjectList.find((dataObject) => dataObject.dataObjectId === record.dataObjectId));
                }
            },
            onFocus: () => {
                if (selectedRowKeys.length === 0 || selectedRowKeys[0] !== record.dataObjectId) {
                    setSelectedRowKeys([record.dataObjectId]);
                    onDataObjectSelected && onDataObjectSelected(dataObjectList.find((dataObject) => dataObject.dataObjectId === record.dataObjectId));
                }
            },
        };
    };

    const onPageClick = (page, pageSize) => {
        let currentPagination = { ...pagination };
        let currentPaginatedDataObjectList = dataObjectList && dataObjectList.slice((page - 1) * pageSize, page * pageSize);
        setPaginatedDataObjectList(currentPaginatedDataObjectList);
        currentPagination.page = page;
        currentPagination.pageSize = pageSize;
        setPagination(currentPagination);
        if (currentPaginatedDataObjectList.length <= pageSize) {
            onPageSelect && onPageSelect({ current: page, pageSize });
        }
    };

    return (
        <FlexContainerLayout
            showHeader={showHeader}
            header={
                <Space direction="horizontal">
                    <Text type="secondary">Number of records: </Text>
                    <Text type="success">{recordCount || 0}</Text>
                </Space>
            }
            children={
                <Table
                    dataSource={paginatedDataObjectList}
                    size="small"
                    rowKey="dataObjectId"
                    tableLayout="auto"
                    loading={loading ? { size: "large", tip: "Loading DataObjects..." } : false}
                    className="data-objects-preview-table *:h-full *:flex-1"
                    pagination={false}
                    onRow={onClickRow}
                    rowClassName={(dataObject, index) => {
                        if (dataObject && dataObject.errors && dataObject.errors.length > 0) {
                            return "error-row";
                        }
                        return "default-row";
                    }}
                >
                    {[
                        <Column
                            width="8rem"
                            key="status"
                            shouldCellUpdate={(record, prevRecord) => {
                                return record !== prevRecord;
                            }}
                            render={(value, dataObject) => (
                                <Space size={"small"}>
                                    {!dataObject._verificationId ? (
                                        <>
                                            <Tooltip title={"Verification not initiated"}>
                                                <SafetyCertificateTwoTone twoToneColor={"#aeb0ac"} />
                                            </Tooltip>
                                            <Tooltip title={"Consent not requested"}>
                                                <LikeTwoTone twoToneColor={"#aeb0ac"} />
                                            </Tooltip>
                                        </>
                                    ) : dataObject._verificatioExpiry && moment().utc().isAfter(moment(dataObject._verificatioExpiry).utc()) ? (
                                        <>
                                            <Tooltip title={"Verification link expired"}>
                                                <SafetyCertificateTwoTone twoToneColor={"red"} />
                                            </Tooltip>
                                            <Tooltip title={"Consent not requested"}>
                                                <LikeTwoTone twoToneColor={"#aeb0ac"} />
                                            </Tooltip>
                                        </>
                                    ) : !dataObject._verified ? (
                                        <>
                                            <Tooltip title={"Verification link sent"}>
                                                <SafetyCertificateTwoTone />
                                            </Tooltip>
                                            <Tooltip title={"Consent not requested"}>
                                                <LikeTwoTone twoToneColor={"#aeb0ac"} />
                                            </Tooltip>
                                        </>
                                    ) : (
                                        <>
                                            <Tooltip title={"Verified"}>
                                                <SafetyCertificateTwoTone twoToneColor={"#78d11f"} />
                                            </Tooltip>
                                            {dataObject._consent === false ? (
                                                <Tooltip title={"Consent not granted or revoked"}>
                                                    <DislikeTwoTone twoToneColor={"red"} />
                                                </Tooltip>
                                            ) : dataObject._consent === true ? (
                                                <Tooltip title={"Consent granted"}>
                                                    <LikeTwoTone twoToneColor={"#78d11f"} />
                                                </Tooltip>
                                            ) : (
                                                <Tooltip title={"Consent not requested"}>
                                                    <LikeTwoTone twoToneColor={"#aeb0ac"} />
                                                </Tooltip>
                                            )}
                                        </>
                                    )}
                                    {!!onShowDataObjectHistory && (
                                        <HistoryOutlined
                                            aria-label="Data Record History"
                                            className="action-cursor text-blue-600 hover:text-blue-400"
                                            onClick={() => onShowDataObjectHistory(dataObject.dataObjectId)}
                                        />
                                    )}
                                    {dataObject.errors && dataObject.errors.length > 0 ? (
                                        <Tooltip placement="topLeft" title={`Record contains ${dataObject.errors.length} error(s)`}>
                                            <WarningTwoTone twoToneColor="#FF0000" />
                                        </Tooltip>
                                    ) : (
                                        <></>
                                    )}
                                </Space>
                            )}
                        />,
                        <Column
                            width="5%"
                            title="No."
                            key="index"
                            shouldCellUpdate={(record, prevRecord) => {
                                return record !== prevRecord;
                            }}
                            render={(value, item, index) => item.sNo || index + 1}
                        />,
                        <Column
                            width="14rem"
                            title={"Record Id"}
                            key="RecordId"
                            ellipsis={{
                                showTitle: false,
                            }}
                            shouldCellUpdate={(record, prevRecord) => {
                                return record !== prevRecord;
                            }}
                            render={(value, dataObject, index) => {
                                return (
                                    <Tooltip placement="topLeft" title={dataObject.dataObjectId}>
                                        <DataObjectShareOption
                                            key={dataObject.dataObjectId}
                                            dataObjectId={dataObject.dataObjectId}
                                            dataSetId={dataObject.dataSetId}
                                            schemaId={dataObject.schemaId}
                                            businessAreaId={dataObject.businessAreaId}
                                        >
                                            <DataObjectLink dataObjectId={dataObject.dataObjectId}></DataObjectLink>
                                        </DataObjectShareOption>
                                    </Tooltip>
                                );
                            }}
                        />,
                        ...((schemaFields &&
                            schemaFields.map((field) => {
                                return (
                                    <Column
                                        key={field.fieldId}
                                        dataIndex={field.fieldId}
                                        shouldCellUpdate={(record, prevRecord) => {
                                            return record !== prevRecord;
                                        }}
                                        title={<SchemaFieldTitle name={field.name} tags={field.classifiers}></SchemaFieldTitle>}
                                        render={(value, dataObject, index) => {
                                            let displayValue = field.isPII ? maskValue(value) : value;
                                            let fieldError = dataObject.errors?.find((error) => error.name === field.fieldId)?.errors;
                                            return field.isPII ? (
                                                <Tooltip placement="topLeft" title="This value is masked to protect sensitive information.">
                                                    <div>
                                                        <FormDisplayField fieldError={fieldError} value={displayValue} {...field} />
                                                    </div>
                                                </Tooltip>
                                            ) : (
                                                <FormDisplayField fieldError={fieldError} value={displayValue} {...field} />
                                            );
                                        }}
                                    />
                                );
                            })) ||
                            []),
                        <Column
                            width={"4rem"}
                            key={"actions"}
                            shouldCellUpdate={(record, prevRecord) => {
                                return record !== prevRecord;
                            }}
                            render={(value, dataObject) => (
                                <Button
                                    danger
                                    type="text"
                                    aria-label="Remove Record"
                                    className="action-cursor"
                                    onClick={() => onDeleteDataObject(dataObject.dataObjectId)}
                                    icon={<DeleteFilled />}
                                />
                            )}
                        />,
                    ]}
                </Table>
            }
            // Only show footer if the list length is greater than pageSize
            showFooter={dataObjectList?.length > pagination?.pageSize}
            footer={
                <div className="flex w-full justify-start py-1">
                    <Pagination
                        size="small"
                        hideOnSinglePage={true}
                        total={(dataObjectList && dataObjectList.length) || 0}
                        defaultPageSize={pagination.defaultPageSize}
                        pageSize={pagination.pageSize}
                        responsive={true}
                        showSizeChanger={false}
                        onChange={onPageClick}
                    />
                </div>
            }
        ></FlexContainerLayout>
    );
};

export default DataObjectsPreviewList;
